/**
 * Bulk Categorise Panel Component
 * 
 * Inline expandable panel for bulk categorising transactions
 * Similar to the Accounts filter panel
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, CheckCircle, AlertCircle, Loader2, X, Sparkles, ChevronRight, ChevronDown } from 'lucide-react';
import { useBulkCategoriseTransactions, useBulkCategoriseByDescription } from '../../hooks/useTransactions';
import { categoryService } from '../../services/category-service';
import { Category, CategoryType } from '../../types/category.types';
import { Transaction } from '../../types/transaction.types';
import { getCategoryIconComponent } from '../../utils/icon-utils';

interface BulkCategorisePanelProps {
  isExpanded: boolean;
  onToggle: () => void;
  transactionIds: string[];
  transactions: Transaction[]; // Need transactions to determine type filtering
  onComplete: (shouldClose?: boolean) => void; // Called after categorisation, with optional flag to close panel
  onClose: () => void; // Called to close the panel
}

export function BulkCategorisePanel({
  isExpanded,
  onToggle: _onToggle,
  transactionIds,
  transactions,
  onComplete,
  onClose
}: BulkCategorisePanelProps): React.ReactElement {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isAutoCategoriseSelected, setIsAutoCategoriseSelected] = useState<boolean>(true); // Default to selected
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ updated?: number; matched?: number; unmatched?: number; failed: number; errors?: string[] } | null>(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<'all' | CategoryType>('all');

  const bulkCategoriseMutation = useBulkCategoriseTransactions();
  const bulkCategoriseByDescriptionMutation = useBulkCategoriseByDescription();

  // Determine category type filter based on transaction types
  const categoryType = useMemo(() => {
    if (transactions.length === 0) return null;

    // Get the types of selected transactions
    const selectedTransactions = transactions.filter(t => transactionIds.includes(t.id));
    if (selectedTransactions.length === 0) return null;

    // Check if all selected transactions are the same type
    const types = new Set(selectedTransactions.map(t => t.type));
    if (types.size === 1) {
      const type = Array.from(types)[0];
      // Debit transactions should show expense AND transfer categories (e.g., credit card payments are transfers).
      // Credit transactions need to show all categories (income, expense, transfer),
      // so return null to avoid filtering by type.
      return type === 'debit' ? [CategoryType.EXPENSE, CategoryType.TRANSFER] : null;
    }

    // Mixed types - return null to show all categories
    return null;
  }, [transactions, transactionIds]);


  // Fetch categories
  useEffect(() => {
    if (!isExpanded) return;

    const loadCategories = async () => {
      setLoadingCategories(true);
      // Only reset error if we don't have a success state with failures
      // This preserves error messages from categorisation attempts
      if (!success || success.failed === 0) {
        setError(null);
      }
      try {
        // Always fetch all categories and let the UI filter handle filtering
        // This ensures "All" shows all categories and the user can filter by type in the UI
        const params: any = {};
        
        const result = await categoryService.listCategories(params);
        if (result.error) {
          setError(result.error.message);
          setCategories([]);
        } else if (result.data) {
          setCategories(result.data.categories || []);
        } else {
          setCategories([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [isExpanded, categoryType, success]);

  // Filter categories by search query and type
  const filteredCategories = useMemo(() => {
    let filtered = categories;
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(cat => cat.type === typeFilter);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchingCategories = filtered.filter(cat => 
        cat.name.toLowerCase().includes(query) ||
        cat.note?.toLowerCase().includes(query)
      );
      
      // If searching, also include parents of matching children
      // This ensures child categories are visible even if parent doesn't match
      const matchingCategoryIds = new Set(matchingCategories.map(cat => cat.id));
      const parentIdsToInclude = new Set<string>();
      
      matchingCategories.forEach(cat => {
        if (cat.parentId) {
          parentIdsToInclude.add(cat.parentId);
        }
      });
      
      // Add parent categories that have matching children
      const parentsToInclude = filtered.filter(cat => 
        !cat.parentId && parentIdsToInclude.has(cat.id)
      );
      
      // Combine matching categories with their parents
      const allCategoriesToShow = [...matchingCategories];
      parentsToInclude.forEach(parent => {
        if (!matchingCategoryIds.has(parent.id)) {
          allCategoriesToShow.push(parent);
        }
      });
      
      return allCategoriesToShow;
    }
    
    return filtered;
  }, [categories, searchQuery, typeFilter]);

  // Organize categories hierarchically (parent/child)
  const organizedCategories = useMemo(() => {
    const parentCategories = filteredCategories.filter(cat => !cat.parentId);
    const childMap = new Map<string, Category[]>();
    
    filteredCategories.forEach(cat => {
      if (cat.parentId) {
        const children = childMap.get(cat.parentId) || [];
        children.push(cat);
        childMap.set(cat.parentId, children);
      }
    });

    const result: Array<Category & { children?: Category[] }> = [];
    parentCategories.forEach(parent => {
      const children = childMap.get(parent.id) || [];
      result.push({ ...parent, children: children.length > 0 ? children : undefined });
    });

    return result;
  }, [filteredCategories]);

  // Toggle category expansion
  const toggleCategoryExpand = (categoryId: string) => {
    const newExpanded = new Set(expandedCategoryIds);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategoryIds(newExpanded);
  };

  const handleCategorise = async () => {
    if (selectedCategoryId === undefined) {
      setError('Please select a category or choose "Uncategorise"');
      return;
    }

    setError(null);
    setSuccess(null);

    // Filter out any transaction IDs that don't exist in the transactions array (defensive measure)
    const validTransactionIds = transactionIds.filter(id => transactions.some(t => t.id === id));
    
    if (validTransactionIds.length === 0) {
      setError('No valid transactions selected. Please refresh and try again.');
      return;
    }

    try {
      const result = await bulkCategoriseMutation.mutateAsync({
        transactionIds: validTransactionIds,
        categoryId: selectedCategoryId
      });

      if (result) {
        setSuccess(result);
        
        // Only auto-close on complete success (no failures)
        // If there are failures, keep the panel open so user can troubleshoot
        if (result.failed === 0) {
          // Refresh the list
          onComplete(true);
          // Auto-close after 2 seconds
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      } else {
        // Still call onComplete to refresh the list, but don't close the panel
        // Pass false to indicate we should NOT close the panel
        onComplete(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to categorise transactions');
    }
  };

  const handleAutoCategorise = async () => {
    if (!isAutoCategoriseSelected) {
      // If not selected, just select it without executing
      setIsAutoCategoriseSelected(true);
      setSelectedCategoryId(null);
      return;
    }

    setError(null);
    setSuccess(null);

    // Filter out any transaction IDs that don't exist in the transactions array (defensive measure)
    const validTransactionIds = transactionIds.filter(id => transactions.some(t => t.id === id));
    
    if (validTransactionIds.length === 0) {
      setError('No valid transactions selected. Please refresh and try again.');
      return;
    }

    try {
      const result = await bulkCategoriseByDescriptionMutation.mutateAsync({
        transactionIds: validTransactionIds
      });

      if (result) {
        setSuccess(result);
        
        // Only auto-close on complete success (no failures)
        // If there are failures, keep the panel open so user can troubleshoot
        if (result.failed === 0) {
          // Refresh the list
          onComplete(true);
          // Auto-close after 2 seconds
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          // Still call onComplete to refresh the list, but don't close the panel
          // Pass false to indicate we should NOT close the panel
          onComplete(false);
        }
      } else {
        // Still call onComplete to refresh the list, but don't close the panel
        // Pass false to indicate we should NOT close the panel
        onComplete(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to auto-categorise transactions');
    }
  };

  const handleClose = () => {
    setSelectedCategoryId(null);
    setIsAutoCategoriseSelected(true); // Reset to default
    setSearchQuery('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const isLoading = bulkCategoriseMutation.isPending || bulkCategoriseByDescriptionMutation.isPending;
  const selectedCount = transactionIds.length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-6">
          {success ? (
            <div className={`border rounded-lg p-6 ${
              success.failed === 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-start space-x-3">
                {success.failed === 0 ? (
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-medium mb-2 ${
                    success.failed === 0
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-yellow-800 dark:text-yellow-200'
                  }`}>
                    {success.failed === 0 ? 'Categorisation Complete' : 'Categorisation Partially Complete'}
                  </h4>
                  <p className={`text-sm ${
                    success.failed === 0
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-yellow-700 dark:text-yellow-300'
                  }`}>
                    {success.matched !== undefined ? (
                      <>
                        {success.matched} transaction{success.matched !== 1 ? 's' : ''} categorised from previous matches
                        {success.unmatched !== undefined && success.unmatched > 0 && (
                          <span className="block mt-1">
                            {success.unmatched} transaction{success.unmatched !== 1 ? 's' : ''} left uncategorised (no previous match found)
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {success.updated} transaction{success.updated !== 1 ? 's' : ''} categorised successfully
                      </>
                    )}
                    {success.failed > 0 && (
                      <span className="block mt-1">
                        {success.failed} transaction{success.failed !== 1 ? 's' : ''} failed
                      </span>
                    )}
                  </p>
                  {success.failed > 0 && success.errors && success.errors.length > 0 && (
                    <div className="mt-4">
                      <p className={`text-sm font-medium mb-2 ${
                        success.failed === 0
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-yellow-800 dark:text-yellow-200'
                      }`}>
                        Error Details:
                      </p>
                      <ul className={`text-sm space-y-1 max-h-48 overflow-y-auto ${
                        success.failed === 0
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {success.errors.map((error, index) => (
                          <li key={index} className="break-words">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Action Buttons for Failed Categorisations */}
              {success.failed > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium rounded-lg"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      // Reset success state to allow retry
                      setSuccess(null);
                      setSelectedCategoryId(null);
                    }}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Filter and Search */}
              <div className="mb-6 flex flex-col sm:flex-row gap-3">
                <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Type:</span>
                    <div className="flex items-center gap-4 flex-wrap">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="categoryTypeFilter"
                          value="all"
                          checked={typeFilter === 'all'}
                          onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className={`text-sm ${typeFilter === 'all' ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                          All
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="categoryTypeFilter"
                          value={CategoryType.EXPENSE}
                          checked={typeFilter === CategoryType.EXPENSE}
                          onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className={`text-sm ${typeFilter === CategoryType.EXPENSE ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                          Expense
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="categoryTypeFilter"
                          value={CategoryType.INCOME}
                          checked={typeFilter === CategoryType.INCOME}
                          onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className={`text-sm ${typeFilter === CategoryType.INCOME ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                          Income
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="categoryTypeFilter"
                          value={CategoryType.TRANSFER}
                          checked={typeFilter === CategoryType.TRANSFER}
                          onChange={(e) => setTypeFilter(e.target.value as 'all' | CategoryType)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className={`text-sm ${typeFilter === CategoryType.TRANSFER ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                          Transfer
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    aria-label="Search categories"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* Loading Categories */}
              {loadingCategories ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading categories...</span>
                </div>
              ) : (
                <>
                  {/* Categories List - Expandable */}
                  {organizedCategories.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 dark:text-gray-400">
                        {searchQuery ? 'No categories found matching your search' : 'No categories available'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-96 overflow-y-auto">
                      {organizedCategories.map((category) => {
                        const IconComponent = getCategoryIconComponent(category);
                        const isSelected = selectedCategoryId === category.id;
                        const hasChildren = category.children && category.children.length > 0;
                        const isExpanded = expandedCategoryIds.has(category.id);

                        // Get type badge color
                        const getTypeBadgeColor = () => {
                          if (category.type === CategoryType.INCOME) {
                            return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                          }
                          if (category.type === CategoryType.TRANSFER) {
                            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                          }
                          return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                        };

                        // Get icon background color
                        const getIconBackgroundColor = () => {
                          if (category.color) {
                            const hex = category.color.replace('#', '');
                            if (hex.length === 6) {
                              const r = parseInt(hex.substring(0, 2), 16);
                              const g = parseInt(hex.substring(2, 4), 16);
                              const b = parseInt(hex.substring(4, 6), 16);
                              if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
                                return `rgba(${r}, ${g}, ${b}, 0.12)`;
                              }
                            }
                          }
                          return 'rgba(99, 102, 241, 0.12)';
                        };

                        return (
                          <div key={category.id} className="border-b border-gray-100 dark:border-gray-700/50">
                            {/* Parent Category */}
                            <div className={`flex items-center gap-3 py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                              isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                            }`}>
                              {/* Expand/Collapse Button */}
                              <button
                                onClick={() => toggleCategoryExpand(category.id)}
                                className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform ${
                                  hasChildren ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 rounded' : 'cursor-default opacity-0'
                                }`}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                aria-expanded={hasChildren ? isExpanded : undefined}
                              >
                                {hasChildren && (
                                  isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
                                  )
                                )}
                              </button>

                              {/* Category Button */}
                              <button
                                onClick={() => {
                                  setSelectedCategoryId(category.id);
                                  setIsAutoCategoriseSelected(false);
                                }}
                                disabled={isLoading}
                                className="flex-1 flex items-center gap-3 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {/* Category Icon */}
                                <div
                                  className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg"
                                  style={{ backgroundColor: getIconBackgroundColor() }}
                                >
                                  {IconComponent ? (
                                    <IconComponent className="w-5 h-5" style={{ color: category.color || undefined }} aria-hidden="true" />
                                  ) : (
                                    <span className="text-sm font-medium" style={{ color: category.color || undefined }}>
                                      {category.name.charAt(0)}
                                    </span>
                                  )}
                                </div>

                                {/* Category Info */}
                                <div className="flex-1 flex items-center justify-between gap-4 min-w-0">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {category.name}
                                      </span>
                                      {hasChildren && (
                                        <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                          {category.children!.length} {category.children!.length === 1 ? 'subcategory' : 'subcategories'}
                                        </span>
                                      )}
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getTypeBadgeColor()}`}>
                                        {category.type === CategoryType.INCOME ? 'Income' : category.type === CategoryType.TRANSFER ? 'Transfer' : 'Expense'}
                                      </span>
                                      {category.isSystem && (
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                          System
                                        </span>
                                      )}
                                    </div>
                                    {category.note && (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                                        {category.note}
                                      </p>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <CheckCircle className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                            </div>

                            {/* Child Categories */}
                            {hasChildren && isExpanded && (
                              <div className="bg-gray-50 dark:bg-gray-800/50">
                                {category.children!.map((child) => {
                                  const ChildIconComponent = getCategoryIconComponent(child);
                                  const isChildSelected = selectedCategoryId === child.id;

                                  const getChildIconBackgroundColor = () => {
                                    if (child.color) {
                                      const hex = child.color.replace('#', '');
                                      if (hex.length === 6) {
                                        const r = parseInt(hex.substring(0, 2), 16);
                                        const g = parseInt(hex.substring(2, 4), 16);
                                        const b = parseInt(hex.substring(4, 6), 16);
                                        if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
                                          return `rgba(${r}, ${g}, ${b}, 0.12)`;
                                        }
                                      }
                                    }
                                    return 'rgba(99, 102, 241, 0.12)';
                                  };

                                  return (
                                    <button
                                      key={child.id}
                                      onClick={() => {
                                        setSelectedCategoryId(child.id);
                                        setIsAutoCategoriseSelected(false);
                                      }}
                                      disabled={isLoading}
                                      className={`w-full flex items-center gap-3 py-2 px-4 pl-12 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left ${
                                        isChildSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                      }`}
                                    >
                                      {/* Child Icon */}
                                      <div
                                        className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg"
                                        style={{ backgroundColor: getChildIconBackgroundColor() }}
                                      >
                                        {ChildIconComponent ? (
                                          <ChildIconComponent className="w-4 h-4" style={{ color: child.color || undefined }} aria-hidden="true" />
                                        ) : (
                                          <span className="text-xs font-medium" style={{ color: child.color || undefined }}>
                                            {child.name.charAt(0)}
                                          </span>
                                        )}
                                      </div>

                                      {/* Child Info */}
                                      <div className="flex-1 flex items-center justify-between gap-4 min-w-0">
                                        <div className="flex-1 min-w-0">
                                          <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                                            {child.name}
                                          </span>
                                          {child.note && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                              {child.note}
                                            </p>
                                          )}
                                        </div>
                                        {isChildSelected && (
                                          <CheckCircle className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Auto-Categorise and Uncategorise Options */}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {/* Auto-Categorise Option */}
                    <button
                      onClick={() => {
                        setIsAutoCategoriseSelected(true);
                        setSelectedCategoryId(null);
                      }}
                      disabled={isLoading}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        isAutoCategoriseSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-800 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">Use Previous Categories</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Categorise based on matching descriptions</p>
                        </div>
                        {isAutoCategoriseSelected && (
                          <CheckCircle className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        )}
                      </div>
                    </button>

                    {/* Uncategorise Option */}
                    <button
                      onClick={() => {
                        setSelectedCategoryId(null);
                        setIsAutoCategoriseSelected(false);
                      }}
                      disabled={isLoading}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        selectedCategoryId === null && !isAutoCategoriseSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100">Uncategorise</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Remove category from selected transactions</p>
                        </div>
                        {selectedCategoryId === null && !isAutoCategoriseSelected && (
                          <CheckCircle className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                    <button
                      onClick={handleClose}
                      disabled={isLoading}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={isAutoCategoriseSelected ? handleAutoCategorise : handleCategorise}
                      disabled={isLoading || (!isAutoCategoriseSelected && selectedCategoryId === undefined)}
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Categorising...</span>
                        </>
                      ) : (
                        <span>{isAutoCategoriseSelected ? 'Auto-Categorise' : 'Categorise'} {selectedCount} Transaction{selectedCount !== 1 ? 's' : ''}</span>
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

